/* eslint-disable @typescript-eslint/no-explicit-any */
import { FunctionComponent, useEffect } from 'react';

import { ConsumerStickyPanel } from 'components/ui/ConsumerStickyPanel';
import { ProfileSearch } from 'components/business/landing-page/ProfileSearch';
import { Theme, css } from '@emotion/react';
import { consumer, core } from '@tock/coretypes';
import { getConcept, getLocality } from 'utils/businessUtils';
import { getDateRange, hasNoAvailability } from 'utils/offerings';
import { gql } from '@apollo/client';
import { separatedOfferingsSelector } from 'selectors/calendarSelectors';
import { useActions } from '@tock/shared/utils/connect';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useGetTockTenConfigsForCurrentBusinessQuery } from 'generated/graphql';
import { useInView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';
import About from 'components/business/landing-page/About';
import BallPulse from '@tock/shared/components/BallPulse';
import ConsumerPageHeading from 'components/ui/ConsumerPageHeading';
import Metadata from 'components/business/landing-page/Metadata';
import Offerings from 'components/business/offering/Offerings';
import SaveBusiness from './SaveBusiness';
import TockTenCard from 'components/business/landing-page/TockTenCard';
import WalkinWaitlistActionCard from '../walkin-waitlist/WalkinWaitlistActionCard';
import clsx from 'clsx';

gql`
  query GetTockTenConfigsForCurrentBusiness {
    currentBusiness {
      tockTenConfigs {
        id
        name
        description
        image {
          imageUrl
        }
        marketingUrl
      }
    }
  }
`;

interface Props {
  business: consumer.BusinessConfig;
  isLoading: boolean;
  nextRelease?: core.IBookingRelease;
  offerings: consumer.ConsumerOfferings;
  onAddTicketTypeToWaitlist(experienceId?: number): void;
  onMobileSearch(): void;
  onSearch(size: number, date: any, time: any): void;
  onOpenPlaylistDialog(): void;
  ticketGroups: consumer.CalendarTicketGroup[];
}

const ProfileBody: FunctionComponent<Props> = ({
  business,
  isLoading,
  nextRelease,
  offerings,
  onAddTicketTypeToWaitlist,
  onMobileSearch,
  onSearch,
  onOpenPlaylistDialog,
  ticketGroups,
}) => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    rootMargin: '-72px 0px 0px 0px',
  });
  const actions = useActions();
  const separatedOfferings = useSelector(separatedOfferingsSelector);

  const walkinWaitlistEnabled =
    useFeatureFlag('ONLINE_WALKIN_WAITLIST') && business.onlineWalkinWaitlistEnabled;
  const tockTenClientEnabled = useFeatureFlag('TOCK_TEN_CLIENT');
  const { data: tockTenData } = useGetTockTenConfigsForCurrentBusinessQuery({
    skip: !tockTenClientEnabled,
  });

  useEffect(() => {
    actions.actions.navigation.incrementalUpdate({
      hasBack: true,
      hasLogo: false,
    });
  }, [actions.actions.navigation]);

  const { minDate } = getDateRange(offerings);
  const hasOfferings = !!minDate && (!!offerings || !!separatedOfferings);
  let soldOut = false;

  if (offerings) {
    soldOut = hasNoAvailability(offerings);
  }

  const { priceRange } = business;
  const locality = getLocality(business);
  const subtitle = [locality, getConcept(business), priceRange].filter(Boolean).join(' · ');
  const stickyPanelClass = clsx('ProfileSearch-panel', {
    ConsumerStickyPanelExt: walkinWaitlistEnabled,
  });

  return (
    <>
      <div className="ProfileBody-content">
        <div
          css={(theme: Theme) => css`
            display: flex;
            justify-content: space-between;
            margin-right: ${theme.spacing(6)}px;
            margin-bottom: ${theme.spacing(2.25)}px;

            ${theme.breakpoints.up('sm')} {
              margin-bottom: ${theme.spacing(3)}px;
            }
          `}
        >
          <ConsumerPageHeading ref={ref} title={business.name} subtitle={subtitle} />
          <div
            css={(theme: Theme) => css`
              display: none;
              ${theme.breakpoints.up('sm')} {
                display: block;
              }
            `}
          >
            <SaveBusiness business={business} onOpenDialog={onOpenPlaylistDialog} />
          </div>
        </div>
        <WalkinWaitlistActionCard business={business} className="ProfileBody-waitlistCard" />
        <About business={business} />
        {isLoading && (
          <div className="ProfileBody-contentLoading">
            <BallPulse position="INLINE" />
          </div>
        )}
        {!isLoading && (
          <Offerings
            business={business}
            hasOfferings={hasOfferings}
            nextRelease={nextRelease}
            onAddTicketTypeToWaitlist={onAddTicketTypeToWaitlist}
            separatedOfferings={separatedOfferings}
            ticketGroups={ticketGroups}
            soldOut={soldOut}
          />
        )}
        {tockTenClientEnabled &&
          tockTenData &&
          tockTenData.currentBusiness?.tockTenConfigs.map((config) => {
            const { name, description, image, marketingUrl } = config;

            return (
              <TockTenCard
                name={name}
                description={description}
                imageUrl={image.imageUrl}
                marketingUrl={marketingUrl}
                business={business}
              />
            );
          })}
        {/* TODO: Revisit destructuring style method below on <Metadata /> */}
        <Metadata {...business} address={business} />
      </div>

      <ConsumerStickyPanel className={stickyPanelClass}>
        <ProfileSearch
          business={business}
          hasOfferings={hasOfferings}
          isLoading={isLoading}
          nextRelease={nextRelease}
          offerings={offerings}
          onMobileSearch={onMobileSearch}
          onSearch={onSearch}
          soldOut={soldOut}
          showBusinessCard={!inView}
          locality={locality}
        />
        <WalkinWaitlistActionCard business={business} className="ProfileSearch-waitlistCard" />
      </ConsumerStickyPanel>
    </>
  );
};

export default ProfileBody;
